/**
 * batch-70 wagenpark-week (FO-529, spec 229) - pure regels voor tankpassen.
 * I/O-vrij; de API-laag laadt de data in bulk.
 *
 *   - `tankpasHouderOpDatum`: de houder van een pas op de tankdatum, uit de
 *     houderhistorie (TankpasHouderPeriode). Met tijd telt het moment, zonder tijd
 *     het hele NL-etmaal; twee houders op die dag = ambigu (niet gokken), tenzij
 *     precies één tijdelijke bestuurder (een vervangende auto wint, zoals FO-155).
 *   - `bepaalTankRegime`: BIJTELLING, GEEN_PRIVE of ONBEKEND uit de bijtellingskeuze,
 *     de gebruiksverklaring en de contractmutatie.
 *   - `splitsBrandstofKosten`: privé/zakelijk naar de km-verdeling van de week;
 *     een expliciet privé gemarkeerde regel blijft volledig privé.
 *   - `controleerVerbruik` en `controleerTankinhoud`: liters tegen gereden en
 *     geplande km, de norm (de KmVerbruiksfactor) en het eigen gemiddelde.
 *   - `tel500KmGrens`: de teller van bevestigde privé-km per kalenderjaar.
 *   - `isTankbeurtBuitenPlanning`: weekend zonder dienst, buiten werktijd, ver van
 *     de planning van de dag.
 *
 * Bedragen worden in centen gerekend en pas op het eind naar euro's afgerond.
 */

import {
  GRENS_500KM_NIVEAUS,
  type SplitsingBron,
  type TankRegime,
  type TankpasVerbruikHint,
  type VerdelingBron,
} from '@streep/types'
import { amsterdamAllDayStartToUtc, utcToAmsterdamWallclock } from './amsterdam-tijd'
import { haversineKm } from './targeting-distance'

const rond1 = (n: number): number => Math.round(n * 10) / 10
const rond2 = (n: number): number => Math.round(n * 100) / 100

// ─── Houder op de tankdatum ──────────────────────────────────────────────────

export interface HouderPeriodeKort {
  employeeId: string
  vanaf: Date
  /** Exclusief; null = lopend. */
  tot: Date | null
  /** VAST | TIJDELIJK | POOL */
  soort: string
}

function volgendeDag(ymd: string): string {
  const d = new Date(`${ymd}T12:00:00.000Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}

/**
 * De houder van een pas op het moment van tanken (FO-529 §B5). De periodes komen
 * uit `tankpas_houder_periodes` (geladen door de aanroeper); dit is de enige bron
 * voor de berijder van een factuurregel met een pas, nooit de open toewijzing.
 */
export function tankpasHouderOpDatum(
  perioden: readonly HouderPeriodeKort[],
  datum: Date,
  tijdBekend: boolean,
): { employeeId: string | null; ambigu: boolean } {
  const t = datum.getTime()
  let actief: HouderPeriodeKort[]
  if (tijdBekend) {
    actief = perioden.filter((p) => p.vanaf.getTime() <= t && (p.tot == null || p.tot.getTime() > t))
  } else {
    const ymd = utcToAmsterdamWallclock(datum).ymd
    const start = amsterdamAllDayStartToUtc(ymd).getTime()
    const eind = amsterdamAllDayStartToUtc(volgendeDag(ymd)).getTime()
    actief = perioden.filter((p) => p.vanaf.getTime() < eind && (p.tot == null || p.tot.getTime() > start))
  }
  if (actief.length === 0) return { employeeId: null, ambigu: false }
  const verschillend = new Set(actief.map((p) => p.employeeId))
  if (verschillend.size === 1) return { employeeId: actief[0]!.employeeId, ambigu: false }
  const tijdelijk = actief.filter((p) => p.soort === 'TIJDELIJK')
  if (new Set(tijdelijk.map((p) => p.employeeId)).size === 1) return { employeeId: tijdelijk[0]!.employeeId, ambigu: false }
  return { employeeId: null, ambigu: true }
}

// ─── Regime ──────────────────────────────────────────────────────────────────

export interface RegimeInvoer {
  /** De bijtellingskeuze (campagne) die op de datum geldt. */
  keuze: 'BIJTELLING' | 'GEEN_PRIVEGEBRUIK' | 'UITSLUITEND_ZAKELIJK' | null
  /** De gebruiksverklaring die op de datum geldt. */
  verklaring: 'UITSLUITEND_ZAKELIJK' | 'OOK_PRIVE' | null
  /** `EmployeeContractMutation.bijtelling` op de datum (null = niet ingevuld). */
  contractBijtelling: boolean | null
}

export interface RegimeUitkomst {
  regime: TankRegime
  bron: 'KEUZE' | 'VERKLARING' | 'CONTRACT' | null
}

/**
 * Het gebruiksregime van een houder op een datum (FO-529 §C7). Volgorde: de
 * jaarlijkse bijtellingskeuze (de meest recente expliciete keuze), dan de
 * gebruiksverklaring, dan de bijtelling op de contractmutatie. Alleen "ja" op het
 * contract beslist (bijtelling); "nee" zonder verklaring of keuze is niet genoeg
 * voor "geen privé" (daarvoor is een verklaring nodig): ONBEKEND, en HR legt vast.
 */
export function bepaalTankRegime(input: RegimeInvoer): RegimeUitkomst {
  if (input.keuze === 'BIJTELLING') return { regime: 'BIJTELLING', bron: 'KEUZE' }
  if (input.keuze === 'GEEN_PRIVEGEBRUIK' || input.keuze === 'UITSLUITEND_ZAKELIJK') return { regime: 'GEEN_PRIVE', bron: 'KEUZE' }
  if (input.verklaring === 'OOK_PRIVE') return { regime: 'BIJTELLING', bron: 'VERKLARING' }
  if (input.verklaring === 'UITSLUITEND_ZAKELIJK') return { regime: 'GEEN_PRIVE', bron: 'VERKLARING' }
  if (input.contractBijtelling === true) return { regime: 'BIJTELLING', bron: 'CONTRACT' }
  return { regime: 'ONBEKEND', bron: null }
}

// ─── Splitsing privé/zakelijk ────────────────────────────────────────────────

export interface SplitsingInvoer {
  /** De brandstofregels van de week (excl. btw), met de expliciete privé-markering. */
  regels: ReadonlyArray<{ bedrag: number; isPrive: boolean }>
  /** De km-verdeling van de week (FO-528), of null als die er niet is. */
  kmVerdeling: { priveKm: number | null; zakelijkKm: number | null; bron: VerdelingBron } | null
}

export interface SplitsingUitkomst {
  prive: number
  zakelijk: number
  bron: SplitsingBron
}

/**
 * Splits de brandstofkosten van een week (FO-529 §C8, T5/T6). Een regel die
 * expliciet privé is gemarkeerd (vakantie, buitenland) is volledig privé en gaat
 * vóór de verdeling. De rest gaat naar rato van de privé- en zakelijke km van de
 * week (telematica, anders afgeleid). Zonder km-verdeling: de rest is zakelijk
 * (en de bron zegt dat). Het regime bepaalt wat er met privé gebeurt (inbegrepen
 * bij bijtelling, signaal en verrekening bij bevestiging bij geen bijtelling), niet
 * de rekensom: zo is het bedrag "bij bevestiging te verrekenen" altijd zichtbaar.
 */
export function splitsBrandstofKosten(input: SplitsingInvoer): SplitsingUitkomst {
  const centen = (n: number) => Math.round(n * 100)
  let priveCent = 0
  let restCent = 0
  let explicietePrive = false
  for (const r of input.regels) {
    if (r.isPrive) {
      priveCent += centen(r.bedrag)
      explicietePrive = true
    } else {
      restCent += centen(r.bedrag)
    }
  }
  const v = input.kmVerdeling
  const prive = v?.priveKm ?? 0
  const zakelijk = v?.zakelijkKm ?? 0
  if (v && v.bron !== 'GEEN' && prive + zakelijk > 0) {
    const priveDeel = Math.round((restCent * prive) / (prive + zakelijk))
    return {
      prive: (priveCent + priveDeel) / 100,
      zakelijk: (restCent - priveDeel) / 100,
      bron: v.bron === 'TELEMATICA' ? 'TELEMATICA' : 'AFGELEID',
    }
  }
  return { prive: priveCent / 100, zakelijk: restCent / 100, bron: explicietePrive ? 'EXPLICIET' : 'GEEN' }
}

// ─── Verbruik ────────────────────────────────────────────────────────────────

export interface VerbruikInvoer {
  liters: number
  /** Gereden km in de week (FO-528), of tussen tankbeurten; null = onbekend. */
  kmGereden: number | null
  /** Geplande zakelijke km (ritten van de werkbonnen); null = onbekend. */
  kmGepland: number | null
  /** De norm in l/100 km uit de KmVerbruiksfactor van het voertuig; null = geen. */
  normL100: number | null
  /** Het eigen verbruik (l/100 km) van de vorige weken (tot 12). */
  historieL100: readonly number[]
  drempelPct: number
  drempelLiter: number
}

export interface VerbruikUitkomst {
  verbruikL100: number | null
  afwijkingNormPct: number | null
  gemiddeldeL100: number | null
  afwijkingGemiddeldePct: number | null
  verwachteLiters: number | null
  hint: TankpasVerbruikHint | null
}

/** Onder dit aantal km zegt een weekverbruik in procenten te weinig (één tankbeurt). */
const MIN_KM_VOOR_PCT = 100

/**
 * Toets de liters van een week (FO-529 §D11, T9/T11). Drie oorzaak-hints, in deze
 * volgorde: meer liters dan de gereden km verklaren (boven de norm, tanken voor een
 * ander voertuig?), km zonder liters (de geplande km vragen veel meer liters dan er
 * getankt is: elders of met een andere pas getankt?) en een sterk gestegen eigen
 * verbruik. Een afwijking is meer dan het percentage OF meer dan het aantal liters;
 * het percentage telt pas vanaf 100 km (een week met één korte rit en een volle tank
 * is geen afwijking).
 */
export function controleerVerbruik(input: VerbruikInvoer): VerbruikUitkomst {
  const verbruik = input.kmGereden != null && input.kmGereden > 0 && input.liters > 0 ? (input.liters / input.kmGereden) * 100 : null
  const norm = input.normL100 != null && input.normL100 > 0 ? input.normL100 : null
  const afwijkingNormPct = verbruik != null && norm != null ? ((verbruik - norm) / norm) * 100 : null
  const hist = input.historieL100.filter((h) => Number.isFinite(h) && h > 0)
  const gemiddelde = hist.length >= 2 ? hist.reduce((s, h) => s + h, 0) / hist.length : null
  const afwijkingGem = verbruik != null && gemiddelde != null ? ((verbruik - gemiddelde) / gemiddelde) * 100 : null
  const verwachteLiters = input.kmGepland != null && input.kmGepland > 0 && norm != null ? (input.kmGepland * norm) / 100 : null

  let hint: TankpasVerbruikHint | null = null
  if (verbruik != null && norm != null && input.kmGereden != null) {
    const teVeel = input.liters - (input.kmGereden * norm) / 100
    const pctTeHoog = input.kmGereden >= MIN_KM_VOOR_PCT && afwijkingNormPct != null && afwijkingNormPct > input.drempelPct
    if (teVeel > 0 && (pctTeHoog || teVeel > input.drempelLiter)) hint = 'MEER_LITERS_DAN_KM'
  }
  // Alleen in een week mét tankbeurten: een week zonder tanken (de tank was nog vol) is geen afwijking.
  if (!hint && verwachteLiters != null && input.liters > 0) {
    const tekort = verwachteLiters - input.liters
    const pctTeLaag = input.kmGepland! >= MIN_KM_VOOR_PCT && (tekort / verwachteLiters) * 100 > input.drempelPct
    if (tekort > 0 && (tekort > input.drempelLiter || pctTeLaag)) hint = 'KM_ZONDER_LITERS'
  }
  if (!hint && verbruik != null && gemiddelde != null && afwijkingGem != null && input.kmGereden != null && input.kmGereden >= MIN_KM_VOOR_PCT) {
    const extraLiters = input.liters - (input.kmGereden * gemiddelde) / 100
    if (afwijkingGem > input.drempelPct && extraLiters > input.drempelLiter / 3) hint = 'VERBRUIK_GESTEGEN'
  }
  return {
    verbruikL100: verbruik == null ? null : rond2(verbruik),
    afwijkingNormPct: afwijkingNormPct == null ? null : rond2(afwijkingNormPct),
    gemiddeldeL100: gemiddelde == null ? null : rond2(gemiddelde),
    afwijkingGemiddeldePct: afwijkingGem == null ? null : rond2(afwijkingGem),
    verwachteLiters: verwachteLiters == null ? null : rond1(verwachteLiters),
    hint,
  }
}

/** Een tankbeurt met meer liters dan de tank bevat is direct een signaal (FO-529 §D11, T10). */
export function controleerTankinhoud(liters: number, tankInhoudLiter: number | null): boolean {
  return tankInhoudLiter != null && tankInhoudLiter > 0 && liters > tankInhoudLiter
}

/** De norm in l/100 km uit een factor in km per liter (KmVerbruiksfactor): 13 km/l = 7,69 l/100. */
export function normL100UitKmPerLiter(kmPerLiter: number | null): number | null {
  return kmPerLiter != null && kmPerLiter > 0 ? rond2(100 / kmPerLiter) : null
}

// ─── 500 km-grens ────────────────────────────────────────────────────────────

export interface Grens500Uitkomst {
  totaalKm: number
  pct: number
  /** Het hoogste bereikte meldniveau (60, 90 of 100) of null. */
  niveau: (typeof GRENS_500KM_NIVEAUS)[number] | null
  /** Bij 90% of meer: advies bijtelling overwegen (geen automatische omzetting). */
  adviesBijtelling: boolean
}

/**
 * De teller van bevestigde privé-km bij geen bijtelling per kalenderjaar (FO-529
 * §C9, T7/T8). 460 van 500 = 92%: niveau 90 met het advies om bijtelling te
 * overwegen. Alleen bevestigd privégebruik telt; een open of toegelicht signaal niet.
 */
export function tel500KmGrens(input: { bevestigdePriveKm: readonly number[]; grens: number }): Grens500Uitkomst {
  const totaal = rond1(input.bevestigdePriveKm.reduce((s, k) => s + Math.max(0, k), 0))
  const pct = input.grens > 0 ? rond1((totaal / input.grens) * 100) : 0
  let niveau: Grens500Uitkomst['niveau'] = null
  for (const n of GRENS_500KM_NIVEAUS) if (pct >= n) niveau = n
  return { totaalKm: totaal, pct, niveau, adviesBijtelling: pct >= 90 }
}

// ─── Buiten de planning ──────────────────────────────────────────────────────

export interface Coordinaten {
  lat: number
  lng: number
}


/** Plaatsnaam vergelijkbaar maken ("'s-Hertogenbosch", "DEN BOSCH " en dergelijke blijven verschillend). */
export function normaliseerPlaats(plaats: string | null | undefined): string | null {
  if (!plaats) return null
  const p = plaats.trim().toLowerCase().replace(/\s+/g, ' ')
  return p.length > 0 ? p : null
}

export interface BuitenPlanningInvoer {
  moment: Date
  /** Plaatsnaam van het tankstation (alleen de plaats, nooit een adres). */
  tankPlaats: string | null
  /** Benaderde coördinaten van de plaats (bijvoorbeeld uit bekende locaties), of null. */
  tankCoords: Coordinaten | null
  /** De locaties van de planning van die dag (klant, en het woonadres). */
  locaties: ReadonlyArray<{ plaats: string | null; coords: Coordinaten | null }>
  /** Heeft de houder die dag een dagregel (dienst, piket)? */
  heeftDienst: boolean
  /** Begin en einde van de dienst (HH:mm), als bekend. */
  dienstTijden: { start: string; eind: string } | null
  maxAfstandKm: number
}

export interface BuitenPlanningUitkomst {
  weekendZonderDienst: boolean
  buitenWerktijd: boolean
  /** BINNEN = op of nabij de planning; BUITEN = verder dan de drempel; ONBEKEND = niet te bepalen. */
  locatie: 'BINNEN' | 'BUITEN' | 'ONBEKEND'
  afstandKm: number | null
}

const MARGE_MIN = 60

function minutenVan(hm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(hm)
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}

/**
 * Is een tankbeurt opvallend ten opzichte van de planning (FO-529 §B6/§C9)? Drie
 * vlaggen: weekend zonder dienst, buiten werktijd (geen dienst op een werkdag, of
 * meer dan een uur voor het begin of na het einde) en de afstand tot de dichtstbij-
 * zijnde geplande locatie of het woonadres (luchtlijn tussen plaatsen; met alleen
 * een plaatsnaam is een gelijke plaats "binnen", anders onbekend, nooit een gok).
 */
export function isTankbeurtBuitenPlanning(input: BuitenPlanningInvoer): BuitenPlanningUitkomst {
  const { ymd, hm } = utcToAmsterdamWallclock(input.moment)
  const dow = new Date(`${ymd}T12:00:00.000Z`).getUTCDay()
  const weekend = dow === 0 || dow === 6
  const weekendZonderDienst = weekend && !input.heeftDienst
  let buitenWerktijd = false
  if (!weekend) {
    if (!input.heeftDienst) buitenWerktijd = true
    else if (input.dienstTijden) {
      const nu = minutenVan(hm)
      const start = minutenVan(input.dienstTijden.start)
      const eind = minutenVan(input.dienstTijden.eind)
      if (nu != null && start != null && eind != null) {
        // Een nachtdienst (eind voor begin) loopt door na middernacht: dan valt alles binnen.
        if (eind > start) buitenWerktijd = nu < start - MARGE_MIN || nu > eind + MARGE_MIN
      }
    }
  }
  let afstandKm: number | null = null
  let locatie: BuitenPlanningUitkomst['locatie'] = 'ONBEKEND'
  const metCoords = input.locaties.filter((l): l is { plaats: string | null; coords: Coordinaten } => l.coords != null)
  if (input.tankCoords && metCoords.length > 0) {
    afstandKm = rond1(Math.min(...metCoords.map((l) => haversineKm(input.tankCoords!.lat, input.tankCoords!.lng, l.coords.lat, l.coords.lng))))
    locatie = afstandKm > input.maxAfstandKm ? 'BUITEN' : 'BINNEN'
  } else {
    const tank = normaliseerPlaats(input.tankPlaats)
    if (tank && input.locaties.some((l) => normaliseerPlaats(l.plaats) === tank)) locatie = 'BINNEN'
  }
  return { weekendZonderDienst, buitenWerktijd, locatie, afstandKm }
}

/**
 * Alleen de plaatsnaam uit een tankstation-omschrijving ("SHELL A12 BUNNIK" ->
 * "BUNNIK"): het laatste woord dat geen weg- of merkcode is. Nooit een adres of
 * coördinaat bewaren of tonen (privacy, FO-529 §11).
 */
export function plaatsUitStation(station: string | null | undefined): string | null {
  if (!station) return null
  const woorden = station
    .replace(/[^A-Za-zÀ-ÿ'\- ]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2 && !/^[AN]\d+$/i.test(w))
  const laatste = woorden[woorden.length - 1]
  return laatste ? laatste.toUpperCase() : null
}
